/**
 * The browser side of Inception's rendered reading voice
 * (lib/narrator/brand-voice.ts): where a clip comes from, and how it plays.
 *
 *   ClipSource      a clip's URL: straight from storage when it was rendered
 *                   before (no request to us at all), else rendered on demand
 *                   by /api/narrator/speech. Shared by playback and prefetch.
 *   ClipPlayer      one <audio> element, unlocked on the Listen tap (iOS only
 *                   lets a page play sound it started inside a gesture), that
 *                   plays a clip from any second and reports where it is.
 *   PcmStreamPlayer a spoken answer, played through Web Audio as its PCM
 *                   arrives, so it starts about two seconds after the text.
 *   askReader       a question to /api/articles/ask, spoken or plain.
 */
import type { LanguageType } from 'shared/src/types/languages.type';
import {
  NARRATION_SAMPLE_RATE,
  SPOKEN_ANSWER_CONTENT_TYPE,
  clipIdentity,
  clipPathFromHash,
  publicClipUrl,
  type NarratorLineKey,
} from './brand-voice';

export type ClipRequest =
  | { kind: 'article'; text: string }
  | { kind: 'line'; line: NarratorLineKey; text: string };

async function sha256Hex(text: string): Promise<string | null> {
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (!subtle) return null;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export class ClipSource {
  private memo = new Map<string, Promise<string>>();
  private objectUrls: string[] = [];

  constructor(
    private readonly opts: { lang: LanguageType; articleId: string; storageUrl?: string | null },
  ) {}

  private key(req: ClipRequest): string {
    return req.kind === 'line' ? `line|${req.line}` : `article|${req.text}`;
  }

  /** The clip's playable URL. Concurrent and repeated calls share one load. */
  get(req: ClipRequest): Promise<string> {
    const key = this.key(req);
    let pending = this.memo.get(key);
    if (!pending) {
      pending = this.load(req);
      this.memo.set(key, pending);
      pending.catch(() => {
        if (this.memo.get(key) === pending) this.memo.delete(key);
      });
    }
    return pending;
  }

  /** Warm a clip without waiting for it. */
  prefetch(req: ClipRequest): void {
    this.get(req).catch(() => undefined);
  }

  /** Forget a failed or suspect clip so the next get() tries again. */
  forget(req: ClipRequest): void {
    this.memo.delete(this.key(req));
  }

  release(): void {
    for (const url of this.objectUrls) URL.revokeObjectURL(url);
    this.objectUrls = [];
    this.memo.clear();
  }

  private async load(req: ClipRequest): Promise<string> {
    const direct = await this.inStorage(req.text);
    if (direct) return direct;
    const body =
      req.kind === 'line'
        ? { kind: 'line', lang: this.opts.lang, line: req.line }
        : { kind: 'article', lang: this.opts.lang, articleId: this.opts.articleId, text: req.text };
    const res = await fetch('/api/narrator/speech', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`narrator speech ${res.status}`);
    if ((res.headers.get('content-type') ?? '').startsWith('audio/')) {
      const url = URL.createObjectURL(await res.blob());
      this.objectUrls.push(url);
      return url;
    }
    const json = (await res.json()) as { url?: string };
    if (!json.url) throw new Error('narrator speech: no url');
    return json.url;
  }

  /** The clip's storage URL when it was rendered before, else null. */
  private async inStorage(text: string): Promise<string | null> {
    if (!this.opts.storageUrl) return null;
    try {
      const hash = await sha256Hex(clipIdentity(this.opts.lang, text));
      if (!hash) return null;
      const url = publicClipUrl(this.opts.storageUrl, clipPathFromHash(this.opts.lang, hash));
      const res = await fetch(url, { method: 'HEAD' });
      return res.ok ? url : null;
    } catch {
      return null;
    }
  }
}

/** A 44-byte silent WAV: played inside the Listen tap so later clips may play. */
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YQAAAAA=';

export type PlayResult = 'ended' | 'stopped';

export class ClipPlayer {
  private audio: HTMLAudioElement | null = null;
  private token = 0;
  private settle: ((result: PlayResult) => void) | null = null;

  constructor(private readonly onPlaying?: () => void) {}

  private element(): HTMLAudioElement {
    if (!this.audio) {
      this.audio = new Audio();
      this.audio.preload = 'auto';
    }
    return this.audio;
  }

  /** Call inside a user gesture, before any await. */
  unlock(): void {
    const audio = this.element();
    if (audio.src && audio.src !== SILENT_WAV) return;
    audio.src = SILENT_WAV;
    audio.play().catch(() => undefined);
  }

  /** Play `url` from `from` seconds. Resolves 'ended', or 'stopped' when stop() or another play() cut it. */
  play(url: string, opts: { rate: number; from?: number }): Promise<PlayResult> {
    this.stop();
    const audio = this.element();
    const token = ++this.token;
    return new Promise<PlayResult>((resolve, reject) => {
      this.settle = (result) => {
        this.settle = null;
        resolve(result);
      };
      const mine = () => token === this.token;
      audio.onended = () => {
        if (mine()) this.settle?.('ended');
      };
      audio.onerror = () => {
        if (!mine()) return;
        this.settle = null;
        reject(new Error(`clip failed to play (${audio.error?.code ?? '?'})`));
      };
      audio.onplaying = () => {
        if (mine()) this.onPlaying?.();
      };
      const from = Math.max(0, opts.from ?? 0);
      if (from > 0) {
        audio.addEventListener(
          'loadedmetadata',
          () => {
            if (mine()) audio.currentTime = Math.min(from, Math.max(0, (audio.duration || from) - 0.25));
          },
          { once: true },
        );
      }
      audio.defaultPlaybackRate = opts.rate;
      audio.src = url;
      audio.playbackRate = opts.rate;
      audio.play().catch((error: unknown) => {
        if (!mine()) return;
        this.settle = null;
        reject(error instanceof Error ? error : new Error(String(error)));
      });
    });
  }

  /** Seconds into the current clip. */
  position(): number {
    return this.audio?.currentTime ?? 0;
  }

  setRate(rate: number): void {
    if (!this.audio) return;
    this.audio.defaultPlaybackRate = rate;
    this.audio.playbackRate = rate;
  }

  stop(): void {
    this.token++;
    const settle = this.settle;
    this.settle = null;
    if (this.audio) this.audio.pause();
    settle?.('stopped');
  }
}

type AudioContextCtor = new () => AudioContext;

/** Seconds of answer gathered before the first sound, against network jitter. */
const LEAD_SECONDS = 0.3;
const SCHEDULE_SECONDS = 0.1;

export class PcmStreamPlayer {
  private ctx: AudioContext | null = null;
  private sources: AudioBufferSourceNode[] = [];
  private token = 0;

  /** Call inside a user gesture, before any await. */
  unlock(): void {
    if (typeof window === 'undefined') return;
    // Safari mutes Web Audio with the ringer switch unless the page asks for playback.
    const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
    if (session && session.type !== 'playback') {
      try {
        session.type = 'playback';
      } catch {
        // Older Safari: nothing to set.
      }
    }
    if (!this.ctx) {
      const Ctor =
        (window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor }).AudioContext ??
        (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => undefined);
  }

  /** Play 16-bit mono PCM as it arrives. 'empty' when the stream held no sound or Web Audio is missing. */
  async play(
    stream: ReadableStream<Uint8Array>,
    sampleRate: number = NARRATION_SAMPLE_RATE,
    onStart?: () => void,
  ): Promise<PlayResult | 'empty'> {
    this.stop();
    const token = ++this.token;
    this.unlock();
    const ctx = this.ctx;
    const reader = stream.getReader();
    if (!ctx) {
      reader.cancel().catch(() => undefined);
      return 'empty';
    }
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);

    const bytesPerSecond = sampleRate * 2;
    let pending: Uint8Array[] = [];
    let pendingBytes = 0;
    let carry: number | null = null;
    let next = 0;
    let started = false;

    const schedule = () => {
      if (pendingBytes === 0) return;
      const joined = new Uint8Array(pendingBytes + (carry === null ? 0 : 1));
      let at = 0;
      if (carry !== null) joined[at++] = carry;
      for (const part of pending) {
        joined.set(part, at);
        at += part.length;
      }
      pending = [];
      pendingBytes = 0;
      const usable = joined.length - (joined.length % 2);
      carry = joined.length % 2 ? joined[joined.length - 1] : null;
      if (usable === 0) return;
      const view = new DataView(joined.buffer, 0, usable);
      const samples = new Float32Array(usable / 2);
      for (let i = 0; i < samples.length; i++) samples[i] = view.getInt16(i * 2, true) / 32768;
      const buffer = ctx.createBuffer(1, samples.length, sampleRate);
      buffer.getChannelData(0).set(samples);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      if (!started) {
        started = true;
        next = ctx.currentTime + 0.05;
        onStart?.();
      }
      next = Math.max(next, ctx.currentTime + 0.02);
      source.start(next);
      next += buffer.duration;
      this.sources.push(source);
    };

    for (;;) {
      let result: ReadableStreamReadResult<Uint8Array>;
      try {
        result = await reader.read();
      } catch {
        break;
      }
      if (token !== this.token) {
        reader.cancel().catch(() => undefined);
        return 'stopped';
      }
      if (result.done) break;
      pending.push(result.value);
      pendingBytes += result.value.length;
      const threshold = started ? SCHEDULE_SECONDS : LEAD_SECONDS;
      if (pendingBytes >= threshold * bytesPerSecond) schedule();
    }
    schedule();
    if (!started) return 'empty';
    while (token === this.token && ctx.currentTime < next) {
      await new Promise((resolve) => setTimeout(resolve, Math.max(20, (next - ctx.currentTime) * 1000)));
    }
    if (token !== this.token) return 'stopped';
    this.sources = [];
    return 'ended';
  }

  stop(): void {
    this.token++;
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        // Not started or already stopped.
      }
    }
    this.sources = [];
  }
}

export interface ReaderAnswer {
  answer: string;
  done: boolean;
  /** The answer spoken by the rendered voice, as PCM, when it could start. */
  audio: ReadableStream<Uint8Array> | null;
  sampleRate: number;
}

/** A spoken answer is one JSON line, then PCM: split the two. */
export async function readSpokenAnswer(res: Response): Promise<ReaderAnswer> {
  const sampleRate = Number(res.headers.get('x-narration-sample-rate')) || NARRATION_SAMPLE_RATE;
  if (!(res.headers.get('content-type') ?? '').startsWith(SPOKEN_ANSWER_CONTENT_TYPE) || !res.body) {
    const data = (await res.json()) as { answer?: unknown; done?: unknown };
    return { answer: typeof data.answer === 'string' ? data.answer : '', done: Boolean(data.done), audio: null, sampleRate };
  }
  const reader = res.body.getReader();
  let head = new Uint8Array(0);
  let newline = -1;
  while (newline < 0) {
    const { done, value } = await reader.read();
    if (done) break;
    const merged = new Uint8Array(head.length + value.length);
    merged.set(head);
    merged.set(value, head.length);
    head = merged;
    newline = head.indexOf(10);
  }
  if (newline < 0) throw new Error('spoken answer without a header line');
  const header = JSON.parse(new TextDecoder().decode(head.subarray(0, newline))) as { answer?: unknown; done?: unknown };
  const rest = head.subarray(newline + 1);
  const audio = new ReadableStream<Uint8Array>({
    start(controller) {
      if (rest.length) controller.enqueue(rest);
    },
    async pull(controller) {
      const { done, value } = await reader.read();
      if (done) controller.close();
      else controller.enqueue(value);
    },
    cancel(reason) {
      return reader.cancel(reason);
    },
  });
  return {
    answer: typeof header.answer === 'string' ? header.answer : '',
    done: Boolean(header.done),
    audio,
    sampleRate,
  };
}

export async function askReader(
  body: {
    articleId: string;
    lang: LanguageType;
    locale: string;
    question: string;
    history: Array<{ question: string; answer: string }>;
    readSoFar: string;
    voice: boolean;
  },
): Promise<ReaderAnswer> {
  const res = await fetch('/api/articles/ask', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`article ask ${res.status}`);
  return readSpokenAnswer(res);
}
